import React, { useMemo } from 'react'
import { resolveMediaUrl } from '../../../../utils/mediaUrl'
import AppDropdown from '../../../../components/AppDropdown'
import AppDateInput from '../../../../components/AppDateInput'
import { appToast } from '../../../../components/AppToast'

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  type = 'text',
  disabled = false,
}) {
  if (type === 'date') {
    return (
      <label className="grid gap-1.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {label}
          {required ? <span className="text-error"> *</span> : null}
        </span>
        <AppDateInput
          value={value || ''}
          onChange={(val) => onChange && onChange(val)}
          placeholder={placeholder || 'DD-MM-YYYY'}
          disabled={disabled}
        />
      </label>
    )
  }

  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        value={value || ''}
        onChange={(event) => onChange && onChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-2 text-xs text-on-surface placeholder:text-on-surface-variant/60 outline-none transition-all focus:border-primary focus:bg-surface focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
      />
    </label>
  )
}

export function SelectField({
  label,
  value,
  onChange,
  options = [],
  required = false,
  disabled = false,
  placeholder = 'Select',
}) {
  const normOptions = useMemo(() => {
    const seen = new Set()
    const result = []
    for (const opt of options || []) {
      if (opt === undefined || opt === null) continue
      const item = typeof opt === 'object' ? opt : { value: String(opt), label: String(opt) }
      const keyVal = String(item.value)
      if (!seen.has(keyVal)) {
        seen.add(keyVal)
        result.push(item)
      }
    }
    return result
  }, [options])

  return (
    <div className="grid gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <AppDropdown
        value={value || ''}
        onChange={(val) => onChange && onChange(val)}
        options={normOptions}
        disabled={disabled}
        placeholder={placeholder}
        triggerClassName="!h-9 !rounded-xl !border-outline-variant/30 !bg-surface-container-low !text-xs !text-on-surface hover:!bg-surface-container transition-all"
      />
    </div>
  )
}

export function CheckboxField({ label, checked, onChange, disabled = false }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer mt-6 select-none">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(event) => onChange && onChange(event.target.checked)}
        disabled={disabled}
        className="h-4 w-4 rounded-md border-outline-variant text-primary focus:ring-primary/20 transition-all cursor-pointer disabled:opacity-60"
      />
      <span className="text-xs font-semibold text-on-surface-variant">{label}</span>
    </label>
  )
}

export function TextAreaField({ label, value, onChange, placeholder, disabled = false, rows = 3 }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">{label}</span>
      <textarea
        value={value || ''}
        onChange={(event) => onChange && onChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 py-2.5 text-xs text-on-surface outline-none placeholder:text-on-surface-variant/60 transition-all focus:border-primary focus:bg-surface focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
      />
    </label>
  )
}

export function PhotoUploadField({ label, value, onChange, disabled = false }) {
  const previewSrc = resolveMediaUrl(value)

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      appToast.error('Please upload a valid image file.')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      appToast.error('File size exceeds 2MB limit.')
      return
    }
    const reader = new FileReader()
    reader.onload = (ev) => {
      onChange(ev.target.result)
    }
    reader.readAsDataURL(file)
  }

  const handleRemove = () => {
    onChange('')
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
        {label || 'Student Photo'}
      </span>
      <div className="flex items-start gap-4">
        <div className="relative h-28 w-28 shrink-0 overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-low shadow-sm">
          {previewSrc ? (
            <img src={previewSrc} alt="Student Profile" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center text-on-surface-variant/40">
              <span className="material-symbols-outlined text-3xl">person</span>
              <span className="mt-1 text-[9px] font-bold uppercase tracking-wider">No Photo</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label className="relative inline-flex items-center gap-2 cursor-pointer overflow-hidden rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container transition-all shadow-sm">
            <span className="material-symbols-outlined text-[16px] text-primary">upload</span>
            <span>Choose Photo</span>
            <input
              type="file"
              accept="image/*"
              className="absolute inset-0 cursor-pointer opacity-0"
              onChange={handleFileChange}
              disabled={disabled}
            />
          </label>
          {value && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-xl border border-error/20 bg-error-container/50 px-3 py-1.5 text-xs font-bold text-error transition-all hover:bg-error-container"
            >
              <span className="material-symbols-outlined text-[14px]">delete</span>
              Delete Photo
            </button>
          )}
          <p className="text-[10px] text-on-surface-variant/70">Max size 2MB (JPG, PNG).</p>
        </div>
      </div>
    </div>
  )
}
